import { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import echo from '../services/echo';

const AuthContext = createContext();

function normalizeUserPayload(res) {
  // /user (Resource) => { data: {...} }
  // /login.user (Resource inline) => { user: { data: {...} }, token, ... } OU já normalizado
  if (!res) return null;
  if (res.data && res.data.data) return res.data.data;        // GET /user
  if (res.data && res.data.user && res.data.user.data) return res.data.user.data; // POST /login (resource inline)
  if (res.data && res.data.user) return res.data.user;        // POST /login (objeto simples)
  if (res.data) return res.data;                              // fallback
  return res;
}

export function AuthProvider({ children }) {
  const [user, setUser]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setUser(null);
          setLoading(false);
          return;
        }

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get('/user');
        setUser(normalizeUserPayload(response));
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('auth_token');
          delete api.defaults.headers.common['Authorization'];
        } else {
          console.error('Erro inesperado ao verificar status de autenticação:', error);
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuthStatus();
  }, []);

  // Listener para atualização de permissões via WebSocket
  useEffect(() => {
    if (!user || !user.perfil_id) return;

    const channel = echo.private(`perfil.${user.perfil_id}.permissions`);
    
    channel.listen('.permissions.updated', (event) => {
      console.log('Permissões atualizadas:', event);
      // Atualiza as permissões do usuário atual
      refreshPermissions();
    });

    // Cleanup: desinscrever do canal quando o componente desmontar ou usuário mudar
    return () => {
      echo.leave(`perfil.${user.perfil_id}.permissions`);
    };
  }, [user?.perfil_id]);

  // LOGIN: agora normaliza /user para garantir perfil.permissions
  const login = async (email, password) => {
    try {
      const loginResponse = await api.post('/login', { email, password });
      const { token } = loginResponse.data;

      localStorage.setItem('auth_token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const me = await api.get('/user');
      setUser(normalizeUserPayload(me));

      return { success: true };
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const firstError = error.response.data.errors
          ? Object.values(error.response.data.errors)[0][0]
          : 'Credenciais inválidas.';
        return { success: false, message: firstError };
      }
      console.error('Falha inesperada no login:', error);
      setUser(null);
      return { success: false, message: 'Ocorreu um erro inesperado no servidor. Tente novamente.' };
    }
  };

  const logout = async () => {
    try {
      await api.post('/logout');
    } catch (error) {
      console.error('Falha no logout:', error);
    } finally {
      localStorage.removeItem('auth_token');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
    }
  };

  // Atualiza permissões do usuário atual (útil quando permissões do perfil mudam)
  const refreshPermissions = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await api.get('/user');
      setUser(normalizeUserPayload(response));
      
      console.log('Permissões atualizadas com sucesso');
    } catch (error) {
      console.error('Erro ao atualizar permissões:', error);
      // Se falhar autenticação, fazer logout
      if (error.response && error.response.status === 401) {
        await logout();
      }
    }
  };

  // Métodos de verificação de permissão (RBAC real)
  const hasPermission = (permission) => {
    if (!user) return false;
    const perms = user.perfil?.permissions ?? [];
    return perms.includes('admin.full_access') || perms.includes(permission);
  };

  const hasAnyPermission = (permissions) => {
    if (!user) return false;
    const perms = user.perfil?.permissions ?? [];
    if (perms.includes('admin.full_access')) return true;
    return permissions.some((p) => perms.includes(p));
  };

  const isAdmin = () => {
    const perms = user?.perfil?.permissions ?? [];
    return perms.includes('admin.full_access');
  };

  const canAccess = (resource, action = 'view') => {
    const permission = `${resource}.${action}`;
    return hasPermission(permission);
  };

  // R03: Helper para verificar se é fim de semana ou feriado nacional
  // IMPORTANTE: A lógica de feriados está centralizada no BACKEND (CheckWeekendAccess.php)
  // Este método agora apenas retorna o estado armazenado que veio do servidor
  const isWeekend = () => {
    // Retorna o valor que foi carregado do backend via /server-info
    // Se ainda não foi carregado, assume false (não bloqueia)
    return window.__serverInfo?.is_bloqueado || false;
  };

  // R03: Verifica se o perfil do usuário pode acessar atendimentos no fim de semana
  // IMPORTANTE: Esta regra vale para TODOS os perfis, incluindo Administrador
  const hasWeekendAccess = () => {
    if (!user) return false;
    const perfilNome = user.perfil?.nome ?? '';
    const isPromotoria = perfilNome.toLowerCase().includes('promotoria');
    const isDefensoria = perfilNome.toLowerCase().includes('defensoria');
    return isPromotoria || isDefensoria;
  };

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    refreshPermissions,
    hasPermission,
    hasAnyPermission,
    isAdmin,
    canAccess,
    isWeekend,
    hasWeekendAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
